import {
  addDays,
  diffInCalendarDays,
  getTimelineDateShape,
  parseTimelineDateTime,
  startOfLocalDay,
  TimelineDatedItem,
} from "./timeline-date-utils";

export type TimelineZoom =
  | "hour"
  | "half-day"
  | "day"
  | "week"
  | "month"
  | "quarter"
  | "semester"
  | "year";

export interface TimelineRange {
  start: Date;
  end: Date;
}

export interface TimelineTick {
  key: string;
  start: Date;
  end: Date;
  label: string;
  subLabel: string;
  width: number;
  isWeekend: boolean;
  isToday: boolean;
}

export interface TimelineTimeSegment {
  startHour: number;
  endHour: number;
  width: number;
}

export interface TimelineTimeScale {
  zoom: "hour" | "half-day";
  startHour: number;
  endHour: number;
  segments: TimelineTimeSegment[];
  dayWidth: number;
}

export interface TimelineSpanMetrics {
  left: number;
  width: number;
  continuesBefore: boolean;
  continuesAfter: boolean;
}

export interface BuildTimelineTicksOptions {
  today?: Date;
  locale?: string;
  timeScale?: TimelineTimeScale;
  weekLabelPrefix?: string;
}

export interface TimelineZoomConstraints {
  maxDayTicks: number;
  maxWeekTicks: number;
}

export const TIMELINE_DAY_WIDTH = 36;
export const TIMELINE_WEEK_WIDTH = 112;
export const TIMELINE_MONTH_WIDTH = 120;
export const TIMELINE_QUARTER_WIDTH = 132;
export const TIMELINE_SEMESTER_WIDTH = 144;
export const TIMELINE_YEAR_WIDTH = 168;
export const TIMELINE_HOUR_WIDTH = 56;
export const TIMELINE_HALF_DAY_WIDTH = 84;
export const TIMELINE_ROW_HEIGHT = 46;
export const TIMELINE_DAY_START_HOUR_OPTIONS = Array.from(
  { length: 24 },
  (_, index) => index,
);
export const TIMELINE_DAY_END_HOUR_OPTIONS = Array.from(
  { length: 24 },
  (_, index) => index + 1,
);

const MIN_VISIBLE_DAYS = 14;
export const DEFAULT_TIMELINE_DAY_START_HOUR = 7;
export const DEFAULT_TIMELINE_DAY_END_HOUR = 19;
const MINUTES_PER_HOUR = 60;
const TIME_ZOOM_STEP_HOURS = {
  hour: 1,
  "half-day": 6,
} as const;
const TIME_ZOOM_WIDTHS = {
  hour: TIMELINE_HOUR_WIDTH,
  "half-day": TIMELINE_HALF_DAY_WIDTH,
} as const;
const PERIOD_MONTH_SPANS = {
  month: 1,
  quarter: 3,
  semester: 6,
  year: 12,
} as const;
const PERIOD_WIDTHS = {
  month: TIMELINE_MONTH_WIDTH,
  quarter: TIMELINE_QUARTER_WIDTH,
  semester: TIMELINE_SEMESTER_WIDTH,
  year: TIMELINE_YEAR_WIDTH,
} as const;
const PERIOD_AVERAGE_DAYS = {
  month: 30,
  quarter: 91,
  semester: 182,
  year: 365,
} as const;

type TimeTimelineZoom = keyof typeof TIME_ZOOM_STEP_HOURS;
type PeriodTimelineZoom = keyof typeof PERIOD_MONTH_SPANS;

export const normalizeTimelineRange = (range: TimelineRange): TimelineRange => {
  const start = startOfLocalDay(range.start);
  const end = startOfLocalDay(range.end);

  if (start <= end) {
    return { start, end };
  }

  return { start: end, end: start };
};

const startOfWeek = (date: Date): Date => {
  const value = startOfLocalDay(date);
  const weekday = value.getDay();
  const mondayOffset = weekday === 0 ? -6 : 1 - weekday;
  return addDays(value, mondayOffset);
};

const startOfMonth = (date: Date): Date => {
  const value = startOfLocalDay(date);
  return new Date(value.getFullYear(), value.getMonth(), 1);
};

const endOfMonth = (date: Date): Date => {
  const value = startOfLocalDay(date);
  return new Date(value.getFullYear(), value.getMonth() + 1, 0);
};

const getDaysInMonth = (date: Date): number => endOfMonth(date).getDate();

const getMonthDiff = (start: Date, end: Date): number =>
  (end.getFullYear() - start.getFullYear()) * 12 +
  end.getMonth() -
  start.getMonth();

export const isTimelineTimeZoom = (
  zoom: TimelineZoom,
): zoom is TimeTimelineZoom => zoom in TIME_ZOOM_STEP_HOURS;

const isPeriodTimelineZoom = (zoom: TimelineZoom): zoom is PeriodTimelineZoom =>
  zoom in PERIOD_MONTH_SPANS;

const clampTimelineHour = (hour: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, Math.trunc(hour)));

export const normalizeTimelineDayHours = (
  startHour: number,
  endHour: number,
): { startHour: number; endHour: number } => {
  const normalizedStartHour = clampTimelineHour(startHour, 0, 23);
  const normalizedEndHour = clampTimelineHour(endHour, 1, 24);

  if (normalizedStartHour < normalizedEndHour) {
    return {
      startHour: normalizedStartHour,
      endHour: normalizedEndHour,
    };
  }

  return {
    startHour: Math.max(0, normalizedEndHour - 1),
    endHour: normalizedEndHour,
  };
};

export const formatTimelineHourLabel = (hour: number): string =>
  `${String(hour).padStart(2, "0")}:00`;

const getWorkingDayStart = (date: Date, timeScale?: TimelineTimeScale): Date =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    timeScale?.startHour ?? DEFAULT_TIMELINE_DAY_START_HOUR,
  );

const getWorkingDayEnd = (date: Date, timeScale?: TimelineTimeScale): Date =>
  new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    timeScale?.endHour ?? DEFAULT_TIMELINE_DAY_END_HOUR,
  );

const getSegmentWidth = (
  zoom: TimeTimelineZoom,
  startHour: number,
  endHour: number,
): number => {
  if (zoom === "half-day") {
    return TIMELINE_HALF_DAY_WIDTH;
  }

  return (endHour - startHour) * TIMELINE_HOUR_WIDTH;
};

const createTimelineTimeScale = (
  zoom: TimeTimelineZoom,
  startHour: number,
  endHour: number,
  segments: Array<Omit<TimelineTimeSegment, "width">>,
): TimelineTimeScale => {
  const fullSegments = segments.map((segment) => ({
    ...segment,
    width: getSegmentWidth(zoom, segment.startHour, segment.endHour),
  }));

  return {
    zoom,
    startHour,
    endHour,
    segments: fullSegments,
    dayWidth: fullSegments.reduce((sum, segment) => sum + segment.width, 0),
  };
};

export const buildTimelineTimeScale = (
  zoom: TimelineZoom,
  startHour = DEFAULT_TIMELINE_DAY_START_HOUR,
  endHour = DEFAULT_TIMELINE_DAY_END_HOUR,
): TimelineTimeScale | undefined => {
  if (!isTimelineTimeZoom(zoom)) {
    return undefined;
  }

  const normalized = normalizeTimelineDayHours(startHour, endHour);
  const totalHours = normalized.endHour - normalized.startHour;

  return createTimelineTimeScale(
    zoom,
    normalized.startHour,
    normalized.endHour,
    zoom === "hour"
      ? Array.from({ length: totalHours }, (_, index) => ({
          startHour: normalized.startHour + index,
          endHour: normalized.startHour + index + 1,
        }))
      : Array.from(
          { length: Math.ceil(totalHours / TIME_ZOOM_STEP_HOURS["half-day"]) },
          (_, index) => {
            const segmentStart =
              normalized.startHour + index * TIME_ZOOM_STEP_HOURS["half-day"];

            return {
              startHour: segmentStart,
              endHour: Math.min(
                normalized.endHour,
                segmentStart + TIME_ZOOM_STEP_HOURS["half-day"],
              ),
            };
          },
        ),
  );
};

const getDefaultTimelineTimeScale = (
  zoom: TimeTimelineZoom,
): TimelineTimeScale =>
  buildTimelineTimeScale(
    zoom,
    DEFAULT_TIMELINE_DAY_START_HOUR,
    DEFAULT_TIMELINE_DAY_END_HOUR,
  ) as TimelineTimeScale;

const resolveTimelineTimeScale = (
  zoom: TimeTimelineZoom,
  timeScale?: TimelineTimeScale,
): TimelineTimeScale =>
  timeScale?.zoom === zoom ? timeScale : getDefaultTimelineTimeScale(zoom);

const getDateHourValue = (date: Date): number =>
  date.getHours() +
  date.getMinutes() / MINUTES_PER_HOUR +
  date.getSeconds() / 3600 +
  date.getMilliseconds() / 3600000;

const getTimeScaleDayOffset = (
  hourValue: number,
  timeScale: TimelineTimeScale,
): number => {
  let offset = 0;

  for (const segment of timeScale.segments) {
    if (hourValue <= segment.startHour) {
      return offset;
    }

    if (hourValue < segment.endHour) {
      return (
        offset +
        ((hourValue - segment.startHour) /
          (segment.endHour - segment.startHour)) *
          segment.width
      );
    }

    offset += segment.width;
  }

  return offset;
};

const getWorkingTimePosition = (
  date: Date,
  rangeStart: Date,
  zoom: TimeTimelineZoom,
  timeScale?: TimelineTimeScale,
): number => {
  const resolvedTimeScale = resolveTimelineTimeScale(zoom, timeScale);
  const dayOffset =
    diffInCalendarDays(startOfLocalDay(rangeStart), startOfLocalDay(date)) *
    resolvedTimeScale.dayWidth;

  return (
    dayOffset + getTimeScaleDayOffset(getDateHourValue(date), resolvedTimeScale)
  );
};

const getTimeScaleDateAtDayOffset = (
  dayOffset: number,
  timeScale: TimelineTimeScale,
): { hours: number; minutes: number } => {
  let offset = dayOffset;

  for (const segment of timeScale.segments) {
    if (offset < segment.width) {
      const hourValue =
        segment.startHour +
        (offset / segment.width) * (segment.endHour - segment.startHour);
      const stepMinutes =
        timeScale.zoom === "hour"
          ? MINUTES_PER_HOUR
          : (segment.endHour - segment.startHour) * MINUTES_PER_HOUR;
      const relativeMinutes =
        (hourValue - segment.startHour) * MINUTES_PER_HOUR;
      const roundedMinutes =
        segment.startHour * MINUTES_PER_HOUR +
        Math.round(relativeMinutes / stepMinutes) * stepMinutes;

      return {
        hours: Math.floor(roundedMinutes / MINUTES_PER_HOUR),
        minutes: roundedMinutes % MINUTES_PER_HOUR,
      };
    }

    offset -= segment.width;
  }

  const lastSegment = timeScale.segments[timeScale.segments.length - 1];
  return {
    hours: lastSegment.endHour,
    minutes: 0,
  };
};

const getVisibleTimeDateAtPosition = (
  position: number,
  rangeStart: Date,
  zoom: TimeTimelineZoom,
  timeScale?: TimelineTimeScale,
  boundaryMode: "next-start" | "previous-end" = "next-start",
): Date => {
  const resolvedTimeScale = resolveTimelineTimeScale(zoom, timeScale);
  let dayIndex = Math.floor(position / resolvedTimeScale.dayWidth);
  let localPosition = position - dayIndex * resolvedTimeScale.dayWidth;

  if (
    boundaryMode === "previous-end" &&
    position > 0 &&
    Math.abs(localPosition) < 0.0001
  ) {
    dayIndex -= 1;
    localPosition = resolvedTimeScale.dayWidth;
  }

  const date = addDays(startOfLocalDay(rangeStart), dayIndex);
  const { hours, minutes } = getTimeScaleDateAtDayOffset(
    localPosition,
    resolvedTimeScale,
  );

  date.setHours(hours, minutes, 0, 0);

  return date;
};

const getWorkingTimeDateAtUnboundedPosition = (
  position: number,
  rangeStart: Date,
  zoom: TimeTimelineZoom,
  timeScale?: TimelineTimeScale,
  boundaryMode?: "next-start" | "previous-end",
): Date => {
  return getVisibleTimeDateAtPosition(
    position,
    rangeStart,
    zoom,
    timeScale,
    boundaryMode,
  );
};

const getWorkingTimeDateAtPosition = (
  position: number,
  rangeStart: Date,
  zoom: TimeTimelineZoom,
  timeScale?: TimelineTimeScale,
): Date =>
  getWorkingTimeDateAtUnboundedPosition(
    Math.max(0, position),
    rangeStart,
    zoom,
    timeScale,
  );

const isLocalMidnight = (date: Date): boolean =>
  date.getHours() === 0 &&
  date.getMinutes() === 0 &&
  date.getSeconds() === 0 &&
  date.getMilliseconds() === 0;

export const getTimelineVisualDateTime = (
  date: Date,
  edge: "start" | "end",
  timeScale?: TimelineTimeScale,
): Date => {
  if (!isLocalMidnight(date)) {
    return date;
  }

  return edge === "start"
    ? getWorkingDayStart(date, timeScale)
    : getWorkingDayEnd(date, timeScale);
};

export const addTimelineTimeSteps = (
  date: Date,
  stepDelta: number,
  zoom: TimeTimelineZoom,
  timeScale?: TimelineTimeScale,
): Date => {
  if (stepDelta === 0) {
    return new Date(date.getTime());
  }

  const rangeStart = startOfLocalDay(date);
  const position = getWorkingTimePosition(date, rangeStart, zoom, timeScale);

  return getWorkingTimeDateAtUnboundedPosition(
    position + stepDelta * TIME_ZOOM_WIDTHS[zoom],
    rangeStart,
    zoom,
    timeScale,
    stepDelta > 0 ? "previous-end" : "next-start",
  );
};

const getTimelineDateTimeShape = (
  item: TimelineDatedItem,
  timeScale?: TimelineTimeScale,
) => {
  const startDate = parseTimelineDateTime(item.startDate);
  const dueDate = parseTimelineDateTime(item.dueDate);
  const normalizeStart = (date: Date): Date =>
    getTimelineVisualDateTime(date, "start", timeScale);
  const normalizeEnd = (date: Date): Date =>
    getTimelineVisualDateTime(date, "end", timeScale);

  if (startDate && dueDate) {
    const visualStart =
      startDate <= dueDate
        ? normalizeStart(startDate)
        : normalizeStart(dueDate);
    const visualEnd =
      startDate <= dueDate ? normalizeEnd(dueDate) : normalizeEnd(startDate);

    return {
      startDate,
      dueDate,
      visualStart,
      visualEnd,
    };
  }

  if (startDate) {
    return {
      startDate,
      dueDate: null,
      visualStart: normalizeStart(startDate),
      visualEnd: normalizeEnd(startDate),
    };
  }

  if (dueDate) {
    return {
      startDate: null,
      dueDate,
      visualStart: normalizeStart(dueDate),
      visualEnd: normalizeEnd(dueDate),
    };
  }

  return {
    startDate: null,
    dueDate: null,
    visualStart: null,
    visualEnd: null,
  };
};

const startOfPeriod = (date: Date, zoom: PeriodTimelineZoom): Date => {
  const value = startOfLocalDay(date);
  const monthSpan = PERIOD_MONTH_SPANS[zoom];
  const periodMonth = Math.floor(value.getMonth() / monthSpan) * monthSpan;

  return new Date(value.getFullYear(), periodMonth, 1);
};

const endOfPeriod = (date: Date, zoom: PeriodTimelineZoom): Date => {
  const periodStart = startOfPeriod(date, zoom);

  return new Date(
    periodStart.getFullYear(),
    periodStart.getMonth() + PERIOD_MONTH_SPANS[zoom],
    0,
  );
};

const getDaysInPeriod = (date: Date, zoom: PeriodTimelineZoom): number =>
  diffInCalendarDays(startOfPeriod(date, zoom), endOfPeriod(date, zoom)) + 1;

const getPeriodDiff = (
  start: Date,
  end: Date,
  zoom: PeriodTimelineZoom,
): number =>
  getMonthDiff(startOfPeriod(start, zoom), startOfPeriod(end, zoom)) /
  PERIOD_MONTH_SPANS[zoom];

const getPeriodDatePosition = (
  date: Date,
  rangeStart: Date,
  zoom: PeriodTimelineZoom,
): number => {
  const value = startOfLocalDay(date);
  const periodStart = startOfPeriod(value, zoom);
  const periodOffset =
    getPeriodDiff(rangeStart, periodStart, zoom) * PERIOD_WIDTHS[zoom];
  const monthOffset =
    getMonthDiff(periodStart, startOfMonth(value)) *
    (PERIOD_WIDTHS[zoom] / PERIOD_MONTH_SPANS[zoom]);
  const dayOffset =
    (value.getDate() - 1) *
    (PERIOD_WIDTHS[zoom] / PERIOD_MONTH_SPANS[zoom] / getDaysInMonth(value));

  return periodOffset + monthOffset + dayOffset;
};

const getLocalDayProgress = (date: Date): number => {
  const dayStart = startOfLocalDay(date);
  const dayEnd = addDays(dayStart, 1);
  const dayDuration = dayEnd.getTime() - dayStart.getTime();

  if (dayDuration <= 0) {
    return 0;
  }

  return Math.min(
    1,
    Math.max(0, (date.getTime() - dayStart.getTime()) / dayDuration),
  );
};

const getPeriodMomentPosition = (
  date: Date,
  rangeStart: Date,
  zoom: PeriodTimelineZoom,
): number => {
  const value = startOfLocalDay(date);
  const periodStart = startOfPeriod(value, zoom);
  const periodOffset =
    getPeriodDiff(rangeStart, periodStart, zoom) * PERIOD_WIDTHS[zoom];
  const monthOffset =
    getMonthDiff(periodStart, startOfMonth(value)) *
    (PERIOD_WIDTHS[zoom] / PERIOD_MONTH_SPANS[zoom]);
  const dayOffset =
    (value.getDate() - 1 + getLocalDayProgress(date)) *
    (PERIOD_WIDTHS[zoom] / PERIOD_MONTH_SPANS[zoom] / getDaysInMonth(value));

  return periodOffset + monthOffset + dayOffset;
};

const getWeekNumber = (date: Date): number => {
  const value = startOfLocalDay(date);
  const firstDay = new Date(value.getFullYear(), 0, 1);
  const days = Math.floor(
    (value.getTime() - firstDay.getTime()) / (24 * 60 * 60 * 1000),
  );

  return Math.ceil((days + firstDay.getDay() + 1) / 7);
};

const normalizeTimelineLocale = (locale?: string): string =>
  locale?.replace("_", "-") || "it-IT";

const normalizeWeekLabelPrefix = (prefix?: string): string =>
  (prefix?.trim().charAt(0) || "S").toLocaleUpperCase();

const formatCompactDayMonth = (date: Date): string =>
  date.toLocaleDateString("it-IT", {
    day: "2-digit",
    month: "2-digit",
  });

export const computeAutoFitRange = <TItem extends TimelineDatedItem>(
  items: TItem[],
  today = new Date(),
  thresholdDays = 30,
): TimelineRange => {
  const datedBounds = items
    .map(getTimelineDateShape)
    .filter((shape) => shape.visualStart && shape.visualEnd)
    .map((shape) => ({
      start: shape.visualStart as Date,
      end: shape.visualEnd as Date,
    }));

  const todayDate = startOfLocalDay(today);

  if (datedBounds.length === 0) {
    return {
      start: addDays(todayDate, -Math.floor(thresholdDays / 2)),
      end: addDays(todayDate, Math.ceil(thresholdDays / 2)),
    };
  }

  const minStart = datedBounds.reduce(
    (min, bound) => (bound.start < min ? bound.start : min),
    datedBounds[0].start,
  );
  const maxEnd = datedBounds.reduce(
    (max, bound) => (bound.end > max ? bound.end : max),
    datedBounds[0].end,
  );

  let start = addDays(minStart, -7);
  let end = addDays(maxEnd, 7);
  const isTodayNearRange =
    todayDate >= addDays(start, -thresholdDays) &&
    todayDate <= addDays(end, thresholdDays);

  if (isTodayNearRange) {
    start = start < addDays(todayDate, -7) ? start : addDays(todayDate, -7);
    end = end > addDays(todayDate, 7) ? end : addDays(todayDate, 7);
  }

  if (diffInCalendarDays(start, end) < MIN_VISIBLE_DAYS) {
    end = addDays(start, MIN_VISIBLE_DAYS);
  }

  return { start, end };
};

export const getAutoZoom = (range: TimelineRange): TimelineZoom => {
  const normalized = normalizeTimelineRange(range);
  const days = diffInCalendarDays(normalized.start, normalized.end) + 1;

  if (days <= 7) {
    return "hour";
  }

  if (days <= 60) {
    return "day";
  }

  return days <= 180 ? "week" : "month";
};

export const getMinimumTimelineZoom = (
  range: TimelineRange,
  constraints: TimelineZoomConstraints,
): TimelineZoom => {
  const normalized = normalizeTimelineRange(range);
  const days = diffInCalendarDays(normalized.start, normalized.end) + 1;

  if (days <= constraints.maxDayTicks) {
    return "day";
  }

  if (Math.ceil(days / 7) <= constraints.maxWeekTicks) {
    return "week";
  }

  return "month";
};

export const constrainTimelineZoom = (
  range: TimelineRange,
  zoom: TimelineZoom,
  constraints: TimelineZoomConstraints,
): TimelineZoom => {
  const minimumZoom = getMinimumTimelineZoom(range, constraints);
  const zoomDetailOrder: Record<TimelineZoom, number> = {
    hour: 0,
    "half-day": 1,
    day: 2,
    week: 3,
    month: 4,
    quarter: 5,
    semester: 6,
    year: 7,
  };

  return zoomDetailOrder[zoom] < zoomDetailOrder[minimumZoom]
    ? minimumZoom
    : zoom;
};

export const getTimelineDayWidth = (
  zoom: TimelineZoom,
  timeScale?: TimelineTimeScale,
): number => {
  if (isTimelineTimeZoom(zoom)) {
    return resolveTimelineTimeScale(zoom, timeScale).dayWidth;
  }

  if (zoom === "day") {
    return TIMELINE_DAY_WIDTH;
  }

  if (zoom === "week") {
    return TIMELINE_WEEK_WIDTH / 7;
  }

  if (isPeriodTimelineZoom(zoom)) {
    return PERIOD_WIDTHS[zoom] / PERIOD_AVERAGE_DAYS[zoom];
  }

  return TIMELINE_MONTH_WIDTH / 30;
};

export const getTimelineTickWidth = (zoom: TimelineZoom): number => {
  if (isTimelineTimeZoom(zoom)) {
    return TIME_ZOOM_WIDTHS[zoom];
  }

  if (zoom === "day") {
    return TIMELINE_DAY_WIDTH;
  }

  if (zoom === "week") {
    return TIMELINE_WEEK_WIDTH;
  }

  return isPeriodTimelineZoom(zoom)
    ? PERIOD_WIDTHS[zoom]
    : TIMELINE_MONTH_WIDTH;
};

export const getTimelineStepWidth = (zoom: TimelineZoom): number => {
  if (isTimelineTimeZoom(zoom)) {
    return TIME_ZOOM_WIDTHS[zoom];
  }

  return getTimelineDayWidth(zoom);
};

export const getTimelineStepMs = (zoom: TimelineZoom): number => {
  if (isTimelineTimeZoom(zoom)) {
    return TIME_ZOOM_STEP_HOURS[zoom] * 60 * 60 * 1000;
  }

  return 24 * 60 * 60 * 1000;
};

export const getTimelineTicksWidth = (ticks: TimelineTick[]): number =>
  ticks.reduce((sum, tick) => sum + tick.width, 0);

export const getTimelineContentWidth = (
  range: TimelineRange,
  zoom: TimelineZoom,
  timeScale?: TimelineTimeScale,
) => {
  const ticks = buildTimelineTicks(range, zoom, { timeScale });
  return getTimelineTicksWidth(ticks);
};

export const buildTimelineTicks = (
  range: TimelineRange,
  zoom: TimelineZoom,
  options: BuildTimelineTicksOptions = {},
): TimelineTick[] => {
  const normalized = normalizeTimelineRange(range);
  const { today = new Date(), locale, timeScale, weekLabelPrefix } = options;
  const normalizedLocale = normalizeTimelineLocale(locale);
  const normalizedWeekLabelPrefix = normalizeWeekLabelPrefix(weekLabelPrefix);
  const todayDate = startOfLocalDay(today);
  const ticks: TimelineTick[] = [];

  if (isTimelineTimeZoom(zoom)) {
    const totalDays = diffInCalendarDays(normalized.start, normalized.end) + 1;
    const resolvedTimeScale = resolveTimelineTimeScale(zoom, timeScale);

    for (let dayIndex = 0; dayIndex < totalDays; dayIndex += 1) {
      const day = addDays(normalized.start, dayIndex);
      const weekday = day.getDay();

      for (const segment of resolvedTimeScale.segments) {
        const start = new Date(
          day.getFullYear(),
          day.getMonth(),
          day.getDate(),
          segment.startHour,
        );
        const end = new Date(
          day.getFullYear(),
          day.getMonth(),
          day.getDate(),
          segment.endHour,
        );
        const startLabel = String(segment.startHour).padStart(2, "0");
        const endLabel = String(segment.endHour).padStart(2, "0");

        ticks.push({
          key: start.toISOString(),
          start,
          end,
          label:
            zoom === "hour" ? `${startLabel}:00` : `${startLabel}-${endLabel}`,
          subLabel: "",
          width: segment.width,
          isWeekend: weekday === 0 || weekday === 6,
          isToday: today >= start && today < end,
        });
      }
    }

    return ticks;
  }

  if (zoom === "day") {
    const totalDays = diffInCalendarDays(normalized.start, normalized.end) + 1;

    for (let index = 0; index < totalDays; index += 1) {
      const date = addDays(normalized.start, index);
      const weekday = date.getDay();

      ticks.push({
        key: date.toISOString(),
        start: date,
        end: date,
        label: date.toLocaleDateString("it-IT", {
          day: "2-digit",
        }),
        subLabel: date.toLocaleDateString(normalizedLocale, {
          weekday: "short",
        }),
        width: TIMELINE_DAY_WIDTH,
        isWeekend: weekday === 0 || weekday === 6,
        isToday: date.getTime() === todayDate.getTime(),
      });
    }

    return ticks;
  }

  if (isPeriodTimelineZoom(zoom)) {
    let cursor = startOfPeriod(normalized.start, zoom);
    const end = endOfPeriod(normalized.end, zoom);

    while (cursor <= end) {
      const periodEnd = endOfPeriod(cursor, zoom);
      const quarter = Math.floor(cursor.getMonth() / 3) + 1;
      const semester = Math.floor(cursor.getMonth() / 6) + 1;
      const label =
        zoom === "year"
          ? cursor.toLocaleDateString(normalizedLocale, { year: "numeric" })
          : zoom === "semester"
            ? `Sem ${semester}`
            : zoom === "quarter"
              ? `T${quarter}`
              : cursor.toLocaleDateString(normalizedLocale, {
                  month: "short",
                });
      const subLabel =
        zoom === "month"
          ? cursor.toLocaleDateString(normalizedLocale, { year: "numeric" })
          : `${cursor.toLocaleDateString(normalizedLocale, {
              month: "short",
            })}-${periodEnd.toLocaleDateString(normalizedLocale, {
              month: "short",
            })}`;

      ticks.push({
        key: cursor.toISOString(),
        start: cursor,
        end: periodEnd,
        label,
        subLabel,
        width: PERIOD_WIDTHS[zoom],
        isWeekend: false,
        isToday: todayDate >= cursor && todayDate <= periodEnd,
      });

      cursor = new Date(
        cursor.getFullYear(),
        cursor.getMonth() + PERIOD_MONTH_SPANS[zoom],
        1,
      );
    }

    return ticks;
  }

  let cursor = startOfWeek(normalized.start);
  const end = addDays(startOfWeek(normalized.end), 6);

  while (cursor <= end) {
    const weekEnd = addDays(cursor, 6);

    ticks.push({
      key: cursor.toISOString(),
      start: cursor,
      end: weekEnd,
      label: `${normalizedWeekLabelPrefix}${getWeekNumber(cursor)}`,
      subLabel: `${formatCompactDayMonth(cursor)}-${formatCompactDayMonth(
        weekEnd,
      )}`,
      width: TIMELINE_WEEK_WIDTH,
      isWeekend: false,
      isToday: todayDate >= cursor && todayDate <= weekEnd,
    });

    cursor = addDays(cursor, 7);
  }

  return ticks;
};

export const getTimelineDatePosition = (
  date: Date,
  range: TimelineRange,
  zoom: TimelineZoom,
  timeScale?: TimelineTimeScale,
): number => {
  const normalized = normalizeTimelineRange(range);

  if (isTimelineTimeZoom(zoom)) {
    return getWorkingTimePosition(date, normalized.start, zoom, timeScale);
  }

  if (isPeriodTimelineZoom(zoom)) {
    return getPeriodDatePosition(
      date,
      startOfPeriod(normalized.start, zoom),
      zoom,
    );
  }

  const rangeStart =
    zoom === "day" ? normalized.start : startOfWeek(normalized.start);
  return diffInCalendarDays(rangeStart, date) * getTimelineDayWidth(zoom);
};

export const getTimelineMomentPosition = (
  date: Date,
  range: TimelineRange,
  zoom: TimelineZoom,
  timeScale?: TimelineTimeScale,
): number => {
  const normalized = normalizeTimelineRange(range);

  if (isTimelineTimeZoom(zoom)) {
    return getWorkingTimePosition(date, normalized.start, zoom, timeScale);
  }

  if (isPeriodTimelineZoom(zoom)) {
    return getPeriodMomentPosition(
      date,
      startOfPeriod(normalized.start, zoom),
      zoom,
    );
  }

  const rangeStart =
    zoom === "day" ? normalized.start : startOfWeek(normalized.start);
  const dayStart = startOfLocalDay(date);

  return (
    (diffInCalendarDays(rangeStart, dayStart) + getLocalDayProgress(date)) *
    getTimelineDayWidth(zoom, timeScale)
  );
};

export const getTimelineDateAtPosition = (
  position: number,
  range: TimelineRange,
  zoom: TimelineZoom,
  timeScale?: TimelineTimeScale,
): Date => {
  const normalized = normalizeTimelineRange(range);

  if (isTimelineTimeZoom(zoom)) {
    return getWorkingTimeDateAtPosition(
      position,
      normalized.start,
      zoom,
      timeScale,
    );
  }

  const rangeStart =
    zoom === "day"
      ? normalized.start
      : zoom === "week"
        ? startOfWeek(normalized.start)
        : isPeriodTimelineZoom(zoom)
          ? startOfPeriod(normalized.start, zoom)
          : startOfMonth(normalized.start);
  const dayIndex = Math.max(
    0,
    Math.floor(position / getTimelineDayWidth(zoom, timeScale)),
  );

  if (isPeriodTimelineZoom(zoom)) {
    const periodWidth = PERIOD_WIDTHS[zoom];
    const periodIndex = Math.max(0, Math.floor(position / periodWidth));
    const periodStart = new Date(
      rangeStart.getFullYear(),
      rangeStart.getMonth() + periodIndex * PERIOD_MONTH_SPANS[zoom],
      1,
    );
    const periodDayIndex = Math.floor(
      ((position - periodIndex * periodWidth) / periodWidth) *
        getDaysInPeriod(periodStart, zoom),
    );

    return addDays(periodStart, periodDayIndex);
  }

  return addDays(rangeStart, dayIndex);
};

export const getTimelineSpanMetrics = (
  entity: TimelineDatedItem,
  range: TimelineRange,
  zoom: TimelineZoom,
  timeScale?: TimelineTimeScale,
): TimelineSpanMetrics | null => {
  const shape = isTimelineTimeZoom(zoom)
    ? getTimelineDateTimeShape(entity, timeScale)
    : getTimelineDateShape(entity);

  if (!shape.visualStart || !shape.visualEnd) {
    return null;
  }

  const normalized = normalizeTimelineRange(range);
  const resolvedTimeScale = isTimelineTimeZoom(zoom)
    ? resolveTimelineTimeScale(zoom, timeScale)
    : null;
  const firstTimeSegment = resolvedTimeScale?.segments[0] ?? null;
  const lastTimeSegment = resolvedTimeScale
    ? resolvedTimeScale.segments[resolvedTimeScale.segments.length - 1]
    : null;
  const rangeStart = isTimelineTimeZoom(zoom)
    ? new Date(
        normalized.start.getFullYear(),
        normalized.start.getMonth(),
        normalized.start.getDate(),
        firstTimeSegment?.startHour ?? DEFAULT_TIMELINE_DAY_START_HOUR,
      )
    : zoom === "day"
      ? normalized.start
      : zoom === "week"
        ? startOfWeek(normalized.start)
        : isPeriodTimelineZoom(zoom)
          ? startOfPeriod(normalized.start, zoom)
          : startOfMonth(normalized.start);
  const rangeEnd = isTimelineTimeZoom(zoom)
    ? new Date(
        normalized.end.getFullYear(),
        normalized.end.getMonth(),
        normalized.end.getDate(),
        lastTimeSegment?.endHour ?? DEFAULT_TIMELINE_DAY_END_HOUR,
      )
    : zoom === "day"
      ? normalized.end
      : zoom === "week"
        ? addDays(startOfWeek(normalized.end), 6)
        : isPeriodTimelineZoom(zoom)
          ? endOfPeriod(normalized.end, zoom)
          : endOfMonth(normalized.end);

  if (shape.visualEnd < rangeStart || shape.visualStart > rangeEnd) {
    return null;
  }

  const visibleStart =
    shape.visualStart < rangeStart ? rangeStart : shape.visualStart;
  const visibleEnd = shape.visualEnd > rangeEnd ? rangeEnd : shape.visualEnd;
  const startsBeforeVisibleDay =
    isTimelineTimeZoom(zoom) && firstTimeSegment
      ? getDateHourValue(shape.visualStart) < firstTimeSegment.startHour
      : false;
  const endsAfterVisibleDay =
    isTimelineTimeZoom(zoom) && lastTimeSegment
      ? getDateHourValue(shape.visualEnd) > lastTimeSegment.endHour
      : false;
  const continuesBefore =
    shape.visualStart < rangeStart || startsBeforeVisibleDay;
  const continuesAfter = shape.visualEnd > rangeEnd || endsAfterVisibleDay;

  if (isTimelineTimeZoom(zoom)) {
    const left = getWorkingTimePosition(
      visibleStart,
      normalized.start,
      zoom,
      timeScale,
    );
    const right = getWorkingTimePosition(
      visibleEnd,
      normalized.start,
      zoom,
      timeScale,
    );

    return {
      left,
      width: Math.max(getTimelineStepWidth(zoom), right - left),
      continuesBefore,
      continuesAfter,
    };
  }

  if (isPeriodTimelineZoom(zoom)) {
    const left = getPeriodDatePosition(visibleStart, rangeStart, zoom);
    const right = getPeriodDatePosition(
      addDays(visibleEnd, 1),
      rangeStart,
      zoom,
    );

    return {
      left,
      width: Math.max(getTimelineDayWidth(zoom), right - left),
      continuesBefore,
      continuesAfter,
    };
  }

  const dayWidth = getTimelineDayWidth(zoom);

  return {
    left: diffInCalendarDays(rangeStart, visibleStart) * dayWidth,
    width: Math.max(
      dayWidth,
      (diffInCalendarDays(visibleStart, visibleEnd) + 1) * dayWidth,
    ),
    continuesBefore,
    continuesAfter,
  };
};
